import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { CrmContactSelect } from '../../components/CrmContactSelect';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import {
  Calendar, Plus, Clock, Phone, User, X, Loader2, ChevronLeft, ChevronRight,
  Bot, Trash2, PlayCircle, AlertCircle, Search, Workflow,
  PhoneMissed, PhoneIncoming, ExternalLink, Mail, MessageSquare, Send
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────────────
   Status taxonomy — only the 5 outcomes that operators actually filter on:
     • in_progress  → call is currently happening
     • completed    → call ended with a real conversation (>5s)
     • no_answer    → did not pick up / went to voicemail / busy / canceled-by-network
     • failed       → system error or invalid phone — needs operator action
     • cancelled    → operator cancelled before dial
   Internal rows that arrive with status="scheduled" / "dialing" / "missed" are
   transient or aliased: the calendar surfaces them, but they don't get their
   own filter pill (Upcoming has its own dedicated panel; Dialing rolls into
   In progress; Missed is normalised to No answer at boot).
   ────────────────────────────────────────────────────────────────────────── */
const STATUSES = [
  { id: 'in_progress', label: 'In progress', chip: 'bg-amber-400/15 text-amber-300 border-amber-400/30',     dot: 'bg-amber-400 animate-pulse' },
  { id: 'completed',   label: 'Completed',   chip: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30', dot: 'bg-emerald-400' },
  { id: 'no_answer',   label: 'No answer',   chip: 'bg-orange-400/15 text-orange-300 border-orange-400/30',   dot: 'bg-orange-400' },
  { id: 'failed',      label: 'Failed',      chip: 'bg-red-500/15 text-red-300 border-red-500/30',            dot: 'bg-red-500' },
  { id: 'cancelled',   label: 'Cancelled',   chip: 'bg-slate-400/15 text-slate-300 border-slate-400/30',      dot: 'bg-slate-400' },
];
// Helper: returns chip styling for ANY incoming status — including transient
// internals like scheduled/dialing/missed — so the calendar can still render
// them with sensible colours even though they aren't filter pills.
const statusMeta = (s) => {
  const x = STATUSES.find(x => x.id === s);
  if (x) return x;
  if (s === 'scheduled')  return { id: 'scheduled',  label: 'Upcoming',    chip: 'bg-sky-400/15 text-sky-300 border-sky-400/30',     dot: 'bg-sky-400' };
  if (s === 'dialing')    return { id: 'dialing',    label: 'Dialing',     chip: 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30', dot: 'bg-cyan-400 animate-pulse' };
  if (s === 'missed')     return { id: 'missed',     label: 'No answer',   chip: 'bg-orange-400/15 text-orange-300 border-orange-400/30', dot: 'bg-orange-400' };
  if (s === 'sending')    return { id: 'sending',    label: 'Sending',     chip: 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30', dot: 'bg-cyan-400 animate-pulse' };
  if (s === 'sent')       return { id: 'sent',       label: 'Sent',        chip: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30', dot: 'bg-emerald-400' };
  return STATUSES[0];
};
const isTextbackRow = (cb) => (cb?.type || 'call') === 'textback';
// Calendar colour = type (calls blue, texts violet); the small dot keeps the status colour.
const typeChip = (cb) => isTextbackRow(cb)
  ? 'bg-violet-500/15 text-violet-700 dark:text-violet-200 border-violet-500/40'
  : 'bg-blue-500/15 text-blue-700 dark:text-blue-200 border-blue-500/40';

const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => h);
const DAY_NAMES_FULL = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const localKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const ScheduledCallsContent = () => {
  const { subAccountId } = useParams();
  const { api } = useAuth();
  const { subAccount } = useSubAccount();
  const { isDarkMode } = useTheme();

  const [callbacks, setCallbacks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Calendar state
  const [view, setView] = useState('month'); // day | week | month
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all | call | textback
  const [showFor, setShowFor] = useState(null);   // selected callback for detail
  const [showSchedule, setShowSchedule] = useState(false);
  const [editingCallback, setEditingCallback] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // type-CANCEL modal target

  // Theme
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';
  const subtleBg = isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50';

  const fetchAll = useCallback(async () => {
    try {
      const [cbRes, agRes, wfRes] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/scheduled-calls`).catch(() => ({ data: [] })),
        api.get(`/api/sub-accounts/${subAccountId}/agents`).catch(() => ({ data: [] })),
        api.get(`/api/sub-accounts/${subAccountId}/ghl/workflows`).catch(() => ({ data: { workflows: [], connected: false } })),
      ]);
      setCallbacks(cbRes.data || []);
      setAgents(agRes.data || []);
      setWorkflows(wfRes.data?.workflows || []);
      setGhlConnected(!!wfRes.data?.connected);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [api, subAccountId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Live auto-refresh — re-fetch every 20s while the page is visible.
  // Pauses when the tab is hidden (saves bandwidth) and re-fetches immediately
  // when the user comes back.
  useEffect(() => {
    let timer = null;
    const tick = () => { if (!document.hidden) fetchAll(); };
    timer = setInterval(tick, 20000);
    const onVis = () => { if (!document.hidden) fetchAll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchAll]);

  const retry = async (cb) => {
    try {
      // Hit /retry FIRST — the endpoint re-arms the row to status='scheduled',
      // clears failure_reason, and bumps scheduled_at. Don't pre-PUT: the
      // server PUT only allows edits on already-scheduled rows, which would
      // 400 here and short-circuit the retry for the exact failed/no_answer
      // rows this button exists for.
      await api.post(`/api/sub-accounts/${subAccountId}/scheduled-calls/${cb.call_id || cb.scheduled_call_id}/retry`);
      toast.success('Re-queued for retry');
      setShowFor(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    }
  };

  // ── Range computation per view ────────────────────────────────────────────
  const range = useMemo(() => {
    const a = new Date(anchorDate); a.setHours(0,0,0,0);
    if (view === 'day') {
      const start = new Date(a);
      const end = new Date(a); end.setHours(23,59,59,999);
      return { start, end };
    }
    if (view === 'week') {
      const start = new Date(a); start.setDate(a.getDate() - a.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23,59,59,999);
      return { start, end };
    }
    // month — calendar grid (start on Sunday of first week, end on Saturday of last week)
    const first = new Date(a.getFullYear(), a.getMonth(), 1);
    const start = new Date(first); start.setDate(first.getDate() - first.getDay());
    const last = new Date(a.getFullYear(), a.getMonth() + 1, 0);
    const end = new Date(last); end.setDate(last.getDate() + (6 - last.getDay())); end.setHours(23,59,59,999);
    return { start, end };
  }, [anchorDate, view]);

  // ── Filtered + grouped callbacks ──────────────────────────────────────────
  const filtered = useMemo(() => {
    const list = callbacks
      .map(cb => ({
        ...cb,
        contact_phone: cb.contact_phone || cb.phone_number || '',
        _date: cb.scheduled_at ? new Date(cb.scheduled_at) : null,
      }))
      .filter(cb => cb._date && !isNaN(cb._date))
      .filter(cb => typeFilter === 'all' ? true : (typeFilter === 'textback' ? isTextbackRow(cb) : !isTextbackRow(cb)));
    if (statusFilter === 'all') return list;
    // "Completed" also rolls up sent textbacks — one status set, chips show the type
    if (statusFilter === 'completed') return list.filter(cb => ['completed', 'sent'].includes(cb.status || 'scheduled'));
    if (statusFilter === 'in_progress') return list.filter(cb => ['in_progress', 'sending'].includes(cb.status || 'scheduled'));
    return list.filter(cb => (cb.status || 'scheduled') === statusFilter);
  }, [callbacks, statusFilter, typeFilter]);

  const inRange = useMemo(
    () => filtered.filter(cb => cb._date >= range.start && cb._date <= range.end),
    [filtered, range]
  );

  const byDay = useMemo(() => {
    const m = new Map();
    inRange.forEach(cb => {
      const k = localKey(cb._date);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(cb);
    });
    m.forEach(arr => arr.sort((a,b) => a._date - b._date));
    return m;
  }, [inRange]);

  // Upcoming next 5 across whole sub-account (status=scheduled OR dialing,
  // future-OR-overdue). Past-due "scheduled" rows still surface here so the
  // operator can see the queue is backed up.
  const upcoming = useMemo(() => {
    return callbacks
      .filter(c => ['scheduled', 'dialing', 'sending'].includes(c.status) && c.scheduled_at)
      .filter(c => typeFilter === 'all' ? true : (typeFilter === 'textback' ? isTextbackRow(c) : !isTextbackRow(c)))
      .map(c => ({ ...c, contact_phone: c.contact_phone || c.phone_number || '' }))
      .sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
      .slice(0, 5);
  }, [callbacks, typeFilter]);

  // Range stats
  const rangeStats = useMemo(() => {
    const contacts = new Set();
    inRange.forEach(cb => { const ph = cb.contact_phone || cb.phone_number; if (ph) contacts.add(ph); });
    return { total: inRange.length, contacts: contacts.size };
  }, [inRange]);

  // Days that have callbacks (for mini calendar dots)
  const callbackDayKeys = useMemo(() => {
    const s = new Set();
    filtered.forEach(cb => s.add(localKey(cb._date)));
    return s;
  }, [filtered]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = (delta) => {
    const d = new Date(anchorDate);
    if (view === 'day') d.setDate(d.getDate() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setAnchorDate(d);
  };
  const goToday = () => setAnchorDate(new Date());

  const headerLabel = view === 'day'
    ? anchorDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : view === 'week'
    ? `Week of ${range.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${range.end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
    : `${MONTH_NAMES[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;

  // ── Drag & Drop reschedule ────────────────────────────────────────────────
  const onDragStart = (e, cbId) => {
    setDraggedId(cbId);
    try { e.dataTransfer.setData('text/plain', cbId); e.dataTransfer.effectAllowed = 'move'; } catch { /* ignore */ }
  };
  const onDragOverDay = (e, dayKey) => { e.preventDefault(); setDragOverKey(dayKey); };
  const onDropDay = async (e, dayDate) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = draggedId; setDraggedId(null);
    if (!id) return;
    const cb = callbacks.find(c => (c.call_id || c.scheduled_call_id) === id);
    if (!cb) return;
    if (cb.status !== 'scheduled') {
      toast.info('Only upcoming callbacks can be rescheduled');
      return;
    }
    const old = new Date(cb.scheduled_at);
    const next = new Date(dayDate);
    next.setHours(old.getHours(), old.getMinutes(), 0, 0);
    if (sameDay(old, next)) return;
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/scheduled-calls/${id}`, {
        scheduled_at: next.toISOString().replace(/\.\d+Z$/, ''),
      });
      toast.success('Rescheduled');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reschedule');
    }
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const setOutcome = async (cb, status) => {
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/scheduled-calls/${cb.call_id || cb.scheduled_call_id}/status`, { status });
      toast.success(status === 'no_answer' && res.data?.workflow_fired
        ? 'Marked No answer · LeadConnector workflow triggered'
        : `Marked ${status.replace('_', ' ')}`);
      setShowFor(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Status update failed');
    }
  };

  const hardDelete = async (cb) => {
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/scheduled-calls/${cb.call_id || cb.scheduled_call_id}/permanent`);
      toast.success('Callback permanently deleted');
      setShowFor(null);
      setConfirmCancel(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const cancel = async (cb) => {
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/scheduled-calls/${cb.call_id || cb.scheduled_call_id}`);
      toast.success('Callback cancelled');
      setShowFor(null);
      setConfirmCancel(null);
      fetchAll();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const triggerNow = async (cb) => {
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/scheduled-calls/${cb.call_id || cb.scheduled_call_id}/trigger`);
      toast.success(isTextbackRow(cb) ? 'Textback queued — sending within 30 seconds' : 'Call triggered');
      setShowFor(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Trigger failed');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <Loader2 className="text-teal-400 animate-spin" size={36} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} p-4 lg:p-6`} data-testid="callbacks-page">
      <div className="max-w-[1700px] mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className={`text-2xl lg:text-3xl font-display font-semibold ${textPrimary}`} data-testid="callbacks-page-title">Callbacks &amp; Textbacks</h1>
            <p className={`text-sm ${textSecondary} mt-0.5`}>
              Calls and texts your AI promised to send back for {subAccount?.name || 'this sub-account'} — drag to reschedule.
            </p>
            <div className="flex items-center gap-3 mt-2 text-[11px]" data-testid="type-legend">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /><span className={textSecondary}>Callback</span></span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /><span className={textSecondary}>Textback</span></span>
            </div>
          </div>
          <button
            onClick={() => { setEditingCallback(null); setShowSchedule(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-teal-400/25 transition-all"
            data-testid="schedule-callback-btn"
          >
            <Plus size={16} /> Schedule Callback/Textback
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* ── LEFT RAIL ────────────────────────────────────────────── */}
          <div className="space-y-4">
            <MiniCalendar
              anchorDate={anchorDate}
              setAnchorDate={setAnchorDate}
              callbackDayKeys={callbackDayKeys}
              isDarkMode={isDarkMode}
              cardBg={cardBg}
              cardBorder={cardBorder}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              setView={setView}
            />

            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`} data-testid="range-stats-card">
              <div className={`text-[10px] font-bold tracking-widest ${textSecondary} mb-2`}>THIS RANGE</div>
              <div className="flex items-baseline gap-3">
                <div>
                  <div className={`text-2xl font-bold ${textPrimary}`}>{rangeStats.total}</div>
                  <div className={`text-[10px] uppercase ${textSecondary}`}>Follow-ups</div>
                </div>
                <div className="ml-auto text-right">
                  <div className={`text-2xl font-bold ${textPrimary}`}>{rangeStats.contacts}</div>
                  <div className={`text-[10px] uppercase ${textSecondary}`}>Contacts</div>
                </div>
              </div>
            </div>

            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`}>
              <div className={`text-[10px] font-bold tracking-widest ${textSecondary} mb-2`}>TYPE</div>
              <div className="flex flex-col gap-1.5">
                <FilterPill active={typeFilter === 'all'} label="All" count={callbacks.length} onClick={() => setTypeFilter('all')} dot="bg-slate-400" />
                <FilterPill active={typeFilter === 'call'} label="Calls" count={callbacks.filter(c => !isTextbackRow(c)).length} onClick={() => setTypeFilter('call')} dot="bg-teal-400" />
                <FilterPill active={typeFilter === 'textback'} label="Texts" count={callbacks.filter(c => isTextbackRow(c)).length} onClick={() => setTypeFilter('textback')} dot="bg-sky-400" />
              </div>
            </div>

            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`}>
              <div className={`text-[10px] font-bold tracking-widest ${textSecondary} mb-2`}>STATUS</div>
              <div className="flex flex-col gap-1.5">
                <FilterPill active={statusFilter === 'all'} label="All" count={callbacks.length} onClick={() => setStatusFilter('all')} dot="bg-slate-400" />
                {STATUSES.map(s => (
                  <FilterPill
                    key={s.id}
                    active={statusFilter === s.id}
                    label={s.label}
                    count={callbacks.filter(c => {
                      const st = c.status || 'scheduled';
                      if (s.id === 'completed') return st === 'completed' || st === 'sent';
                      if (s.id === 'in_progress') return st === 'in_progress' || st === 'sending';
                      return st === s.id;
                    }).length}
                    onClick={() => setStatusFilter(s.id)}
                    dot={s.dot}
                  />
                ))}
              </div>
            </div>

            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-[10px] font-bold tracking-widest ${textSecondary}`}>UPCOMING (NEXT 5)</div>
              </div>
              {upcoming.length === 0 ? (
                <div className={`text-xs ${textSecondary} py-3 text-center`}>No upcoming callbacks</div>
              ) : (
                <div className="space-y-1.5">
                  {upcoming.map(cb => {
                    const cbDate = new Date(cb.scheduled_at);
                    const overdue = cb.status === 'scheduled' && cbDate < new Date();
                    return (
                    <button
                      key={cb.call_id || cb.scheduled_call_id}
                      onClick={() => { setShowFor(cb); setAnchorDate(new Date(cb.scheduled_at)); }}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg ${overdue ? 'bg-rose-500/10 hover:bg-rose-500/15 border border-rose-500/30' : `${subtleBg} hover:bg-teal-400/10`} text-left transition-colors`}
                      data-testid={`upcoming-${cb.call_id || cb.scheduled_call_id}`}
                    >
                      <div className={`w-1 h-8 rounded-full ${overdue ? 'bg-rose-400' : (['dialing','sending'].includes(cb.status) ? 'bg-cyan-400 animate-pulse' : 'bg-sky-400')} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold ${textPrimary} truncate flex items-center gap-1.5`}>
                          {isTextbackRow(cb) ? <MessageSquare size={10} className="text-sky-400 flex-shrink-0" /> : <Phone size={10} className="text-teal-400 flex-shrink-0" />}
                          {cb.contact_name || cb.contact_phone}
                          {overdue && <span className="text-[8px] font-bold uppercase tracking-wider text-rose-300 bg-rose-500/20 px-1 py-0.5 rounded" data-testid={`overdue-${cb.call_id || cb.scheduled_call_id}`}>Overdue</span>}
                          {cb.status === 'dialing' && <span className="text-[8px] font-bold uppercase tracking-wider text-cyan-300 bg-cyan-500/20 px-1 py-0.5 rounded">Dialing</span>}
                          {cb.status === 'sending' && <span className="text-[8px] font-bold uppercase tracking-wider text-cyan-300 bg-cyan-500/20 px-1 py-0.5 rounded">Sending</span>}
                        </div>
                        <div className={`text-[10px] ${textSecondary}`}>
                          {cbDate.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                    </button>
                  );})}
                </div>
              )}
            </div>
          </div>

          {/* ── MAIN CALENDAR ──────────────────────────────────────── */}
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${cardBorder}`}>
              <div className="flex items-center gap-2">
                <button onClick={() => navigate(-1)} className={`p-1.5 rounded-lg hover:bg-white/5 ${textSecondary}`} data-testid="cal-prev-btn"><ChevronLeft size={16} /></button>
                <button onClick={goToday} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${cardBorder} hover:bg-white/5 ${textPrimary}`} data-testid="cal-today-btn">Today</button>
                <button onClick={() => navigate(1)} className={`p-1.5 rounded-lg hover:bg-white/5 ${textSecondary}`} data-testid="cal-next-btn"><ChevronRight size={16} /></button>
                <div className={`ml-3 text-base font-semibold ${textPrimary}`}>{headerLabel}</div>
              </div>
              <div className={`flex rounded-lg ${inputBg} border ${cardBorder} p-0.5`}>
                {['day','week','month'].map(v => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                      view === v ? 'bg-teal-400 text-[#08090d]' : `${textSecondary} hover:${textPrimary}`
                    }`}
                    data-testid={`cal-view-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {view === 'month' && (
              <MonthGrid
                range={range}
                anchorDate={anchorDate}
                byDay={byDay}
                isDarkMode={isDarkMode}
                cardBorder={cardBorder}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                onSelectCallback={setShowFor}
                onDragStart={onDragStart}
                onDragOverDay={onDragOverDay}
                onDropDay={onDropDay}
                dragOverKey={dragOverKey}
                onDayClick={(d) => { setAnchorDate(d); setView('day'); }}
              />
            )}

            {view === 'week' && (
              <WeekGrid
                range={range}
                byDay={byDay}
                isDarkMode={isDarkMode}
                cardBorder={cardBorder}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                onSelectCallback={setShowFor}
                onDragStart={onDragStart}
                onDragOverDay={onDragOverDay}
                onDropDay={onDropDay}
                dragOverKey={dragOverKey}
              />
            )}

            {view === 'day' && (
              <DayList
                date={anchorDate}
                items={(byDay.get(localKey(anchorDate)) || [])}
                isDarkMode={isDarkMode}
                cardBorder={cardBorder}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                subtleBg={subtleBg}
                onSelect={setShowFor}
              />
            )}
          </div>
        </div>

        {/* Detail drawer */}
        <AnimatePresence>
          {showFor && (
            <CallbackDetailDrawer
              cb={showFor}
              isDarkMode={isDarkMode}
              cardBorder={cardBorder}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              subtleBg={subtleBg}
              workflows={workflows}
              onClose={() => setShowFor(null)}
              onTriggerNow={() => triggerNow(showFor)}
              onMarkComplete={() => setOutcome(showFor, 'completed')}
              onMarkNoAnswer={() => setOutcome(showFor, 'no_answer')}
              onRetry={() => retry(showFor)}
              onCancel={() => setConfirmCancel(showFor)}
              onDelete={() => hardDelete(showFor)}
              onEdit={() => { setEditingCallback(showFor); setShowFor(null); setShowSchedule(true); }}
            />
          )}
        </AnimatePresence>

        {/* Type-CANCEL confirmation modal */}
        <AnimatePresence>
          {confirmCancel && (
            <CancelConfirmModal
              cb={confirmCancel}
              cardBorder={cardBorder}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              inputBg={inputBg}
              onClose={() => setConfirmCancel(null)}
              onConfirm={() => cancel(confirmCancel)}
            />
          )}
        </AnimatePresence>

        {/* Schedule modal */}
        <AnimatePresence>
          {showSchedule && (
            <ScheduleModal
              api={api}
              subAccountId={subAccountId}
              agents={agents}
              workflows={workflows}
              ghlConnected={ghlConnected}
              isDarkMode={isDarkMode}
              cardBorder={cardBorder}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              inputBg={inputBg}
              editing={editingCallback}
              onClose={() => { setShowSchedule(false); setEditingCallback(null); }}
              onSaved={() => { setShowSchedule(false); setEditingCallback(null); fetchAll(); }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
function FilterPill({ active, label, count, onClick, dot }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
        active ? 'bg-teal-400/15 text-teal-200 border border-teal-400/30' : 'border border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
      }`}
      data-testid={`filter-${label.toLowerCase().replace(' ','-')}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="flex-1 text-left">{label}</span>
      <span className="text-[10px] tabular-nums opacity-70">{count}</span>
    </button>
  );
}

function MiniCalendar({ anchorDate, setAnchorDate, callbackDayKeys, isDarkMode, cardBg, cardBorder, textPrimary, textSecondary, setView }) {
  const [cursor, setCursor] = useState(new Date(anchorDate));
  useEffect(() => { setCursor(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1)); }, [anchorDate]);
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const start = new Date(first); start.setDate(first.getDate() - first.getDay());
  const days = [];
  for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(start.getDate() + i); days.push(d); }
  const today = new Date();
  return (
    <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`} data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className={`p-1 rounded ${textSecondary} hover:bg-white/5`}><ChevronLeft size={14} /></button>
        <div className={`text-sm font-semibold ${textPrimary}`}>{MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}</div>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className={`p-1 rounded ${textSecondary} hover:bg-white/5`}><ChevronRight size={14} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {DAY_NAMES_FULL.map(n => <div key={n} className={`text-[9px] ${textSecondary}`}>{n[0]}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = sameDay(d, anchorDate);
          const hasCb = callbackDayKeys.has(localKey(d));
          return (
            <button
              key={i}
              onClick={() => { setAnchorDate(d); setView('day'); }}
              className={`relative h-7 rounded-md text-[11px] flex items-center justify-center transition-colors ${
                isSelected ? 'bg-teal-400 text-[#08090d] font-semibold'
                : isToday ? 'bg-teal-400/15 text-teal-200'
                : !inMonth ? `${textSecondary} opacity-40 hover:bg-white/[0.04]`
                : `${textPrimary} hover:bg-white/[0.06]`
              }`}
              data-testid={`mini-cal-${localKey(d)}`}
            >
              {d.getDate()}
              {hasCb && !isSelected && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-teal-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CallbackChip({ cb, onClick, draggable, onDragStart }) {
  const meta = statusMeta(cb.status || 'scheduled');
  const time = cb._date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const TypeIcon = isTextbackRow(cb) ? MessageSquare : Phone;
  return (
    <button
      draggable={draggable}
      onDragStart={(e) => onDragStart && onDragStart(e, cb.call_id || cb.scheduled_call_id)}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(cb); }}
      className={`w-full flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeChip(cb)} hover:brightness-110 transition truncate`}
      title={`${isTextbackRow(cb) ? 'Textback' : 'Callback'} · ${cb.contact_name || cb.contact_phone} · ${time}`}
      data-testid={`chip-${cb.call_id || cb.scheduled_call_id}`}
    >
      <span className={`w-1 h-1 rounded-full ${meta.dot} flex-shrink-0`} />
      <TypeIcon size={9} className="flex-shrink-0 opacity-80" />
      <span className="truncate">{time} · {cb.contact_name || cb.contact_phone}</span>
    </button>
  );
}

function MonthGrid({ range, anchorDate, byDay, cardBorder, textPrimary, textSecondary, onSelectCallback, onDragStart, onDragOverDay, onDropDay, dragOverKey, onDayClick }) {
  const days = [];
  for (let d = new Date(range.start); d <= range.end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  const today = new Date();
  return (
    <div>
      <div className={`grid grid-cols-7 border-b ${cardBorder}`}>
        {DAY_NAMES_FULL.map(n => (
          <div key={n} className={`px-2 py-1.5 text-[10px] uppercase tracking-wider ${textSecondary}`}>{n}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 auto-rows-fr min-h-[78vh]">
        {days.map((d) => {
          const k = localKey(d);
          const items = byDay.get(k) || [];
          const inMonth = d.getMonth() === anchorDate.getMonth();
          const isToday = sameDay(d, today);
          const isOver = dragOverKey === k;
          return (
            <div
              key={k}
              onClick={() => onDayClick && onDayClick(d)}
              onDragOver={(e) => onDragOverDay(e, k)}
              onDragLeave={() => onDragOverDay({ preventDefault: () => {} }, null)}
              onDrop={(e) => onDropDay(e, d)}
              className={`min-h-[140px] p-1.5 border-r border-b ${cardBorder} cursor-pointer transition-colors ${
                !inMonth ? 'opacity-50' : ''
              } ${isOver ? 'bg-teal-400/10' : 'hover:bg-white/[0.02]'}`}
              data-testid={`day-cell-${k}`}
            >
              <div className={`flex items-center justify-between mb-1`}>
                <span className={`text-[11px] font-semibold ${
                  isToday ? 'text-teal-300' : textPrimary
                }`}>
                  {d.getDate()}
                </span>
                {items.length > 0 && <span className={`text-[9px] ${textSecondary}`}>{items.length}</span>}
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {items.slice(0, 3).map(cb => (
                  <CallbackChip key={cb.call_id || cb.scheduled_call_id} cb={cb} onClick={onSelectCallback} draggable onDragStart={onDragStart} />
                ))}
                {items.length > 3 && <div className={`text-[10px] ${textSecondary} px-1`}>+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ range, byDay, cardBorder, textPrimary, textSecondary, onSelectCallback, onDragStart, onDragOverDay, onDropDay, dragOverKey }) {
  const days = [];
  for (let d = new Date(range.start); d <= range.end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  const today = new Date();
  return (
    <div className="grid grid-cols-7 min-h-[78vh]">
      {days.map(d => {
        const k = localKey(d);
        const items = byDay.get(k) || [];
        const isToday = sameDay(d, today);
        const isOver = dragOverKey === k;
        return (
          <div
            key={k}
            onDragOver={(e) => onDragOverDay(e, k)}
            onDrop={(e) => onDropDay(e, d)}
            className={`border-r ${cardBorder} p-2 transition-colors ${isOver ? 'bg-teal-400/10' : ''}`}
            data-testid={`week-day-${k}`}
          >
            <div className={`mb-2 text-[11px] uppercase ${textSecondary}`}>
              <div>{DAY_NAMES_FULL[d.getDay()]}</div>
              <div className={`text-base font-bold ${isToday ? 'text-teal-300' : textPrimary}`}>{d.getDate()}</div>
            </div>
            <div className="space-y-1">
              {items.length === 0 && <div className={`text-[10px] ${textSecondary}`}>—</div>}
              {items.map(cb => (
                <CallbackChip key={cb.call_id || cb.scheduled_call_id} cb={cb} onClick={onSelectCallback} draggable onDragStart={onDragStart} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayList({ date, items, cardBorder, textPrimary, textSecondary, subtleBg, onSelect }) {
  return (
    <div className="grid grid-cols-[60px_1fr]">
      <div className={`border-r ${cardBorder}`}>
        {HOUR_LABELS.map(h => (
          <div key={h} className={`h-12 px-1.5 text-[9px] ${textSecondary} text-right pt-0.5`}>{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</div>
        ))}
      </div>
      <div className="relative">
        {HOUR_LABELS.map(h => (
          <div key={h} className={`h-12 border-b ${cardBorder}`} />
        ))}
        {items.map(cb => {
          const top = cb._date.getHours() * 48 + (cb._date.getMinutes() / 60) * 48;
          const meta = statusMeta(cb.status || 'scheduled');
          return (
            <button
              key={cb.call_id || cb.scheduled_call_id}
              onClick={() => onSelect(cb)}
              className={`absolute left-2 right-2 rounded-lg p-2 border text-left ${typeChip(cb)} hover:brightness-110 transition`}
              style={{ top, minHeight: 38 }}
              data-testid={`day-event-${cb.call_id || cb.scheduled_call_id}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {isTextbackRow(cb) ? 'Textback' : 'Callback'} · {cb._date.toLocaleTimeString(undefined,{ hour: 'numeric', minute: '2-digit' })}
              </div>
              <div className="text-sm font-semibold truncate">{cb.contact_name || cb.contact_phone}</div>
            </button>
          );
        })}
        {items.length === 0 && (
          <div className={`absolute inset-0 flex items-center justify-center ${textSecondary} text-sm`}>
            No callbacks or textbacks scheduled for {date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
          </div>
        )}
      </div>
    </div>
  );
}

function CallbackDetailDrawer({ cb, cardBorder, textPrimary, textSecondary, subtleBg, workflows, onClose, onTriggerNow, onMarkComplete, onMarkNoAnswer, onRetry, onCancel, onDelete, onEdit }) {
  const isText = isTextbackRow(cb);
  const meta = statusMeta(cb.status || 'scheduled');
  const dt = new Date(cb.scheduled_at);
  const wf = workflows.find(w => w.id === cb.ghl_workflow_id);
  const isOpen = cb.status === 'scheduled' || cb.status === 'in_progress' || cb.status === 'dialing' || cb.status === 'sending';
  const isClosed = cb.status === 'completed' || cb.status === 'cancelled' || cb.status === 'sent';
  const isFailedLike = cb.status === 'failed' || (!isText && (cb.status === 'no_answer' || cb.status === 'missed'));
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="callback-detail-drawer"
    >
      <motion.div
        initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
        className={`w-full max-w-md rounded-2xl bg-white dark:bg-[#0c0d12] border ${cardBorder} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`p-5 border-b ${cardBorder}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border ${meta.chip}`} data-testid={`status-pill-${cb.status}`}>{meta.label.toUpperCase()}</span>
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border ${typeChip(cb)}`} data-testid="followup-type-pill">
                {isText ? <><MessageSquare size={9} /> TEXT</> : <><Phone size={9} /> CALL</>}
              </span>
            </div>
            <button onClick={onClose} className={`p-1 ${textSecondary} hover:${textPrimary}`}><X size={16} /></button>
          </div>
          <div className={`text-xs font-semibold uppercase tracking-wider ${isText ? 'text-violet-600 dark:text-violet-300' : 'text-blue-600 dark:text-blue-300'}`} data-testid="followup-detail-heading">{isText ? 'Textback' : 'Callback'}</div>
          <h3 className={`text-xl font-semibold ${textPrimary}`}>{cb.contact_name || '(no name)'}</h3>
          <div className={`flex items-center gap-2 ${textSecondary} text-sm mt-1`}>
            <Phone size={13} />
            <a href={`tel:${cb.contact_phone || cb.phone_number || ''}`} className="hover:text-teal-300">{cb.contact_phone || cb.phone_number || '—'}</a>
          </div>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <Row icon={Clock} label="Scheduled">
            <div className={textPrimary}>{dt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            {cb.timezone && <div className={`text-[11px] ${textSecondary}`}>{cb.timezone}</div>}
          </Row>
          <Row icon={Bot} label="Agent">
            <div className={textPrimary}>{cb.agent_name || '—'}</div>
          </Row>
          {isText && (
            <Row icon={MessageSquare} label="Channel">
              <div className={textPrimary}>{cb.message_channel || 'SMS'}</div>
            </Row>
          )}
          {isText && cb.reason && (
            <Row icon={Mail} label="Reason">
              <div className={`${textPrimary} text-sm`}>{cb.reason}</div>
            </Row>
          )}
          {isText && cb.sent_message && (
            <div className="rounded-lg p-3 bg-teal-500/10 border border-teal-500/30" data-testid="sent-message">
              <div className="text-[10px] uppercase tracking-wider text-teal-300 mb-1">Message sent{cb.sent_at ? ` · ${new Date(cb.sent_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</div>
              <div className={`text-sm ${textPrimary} whitespace-pre-wrap`}>{cb.sent_message}</div>
            </div>
          )}
          {cb.notes && (
            <Row icon={Mail} label="Notes">
              <div className={`${textPrimary} text-sm`}>{cb.notes}</div>
            </Row>
          )}
          {cb.trigger_workflow_on_miss && (
            <Row icon={Workflow} label="On miss">
              <div className={textPrimary}>
                Triggers LeadConnector workflow <b className="text-teal-300">{wf?.name || cb.ghl_workflow_id || '(unknown)'}</b>
                {cb.workflow_fired && <span className="ml-2 text-[10px] text-emerald-300">· FIRED</span>}
              </div>
            </Row>
          )}
          {cb.ghl_contact_id && (
            <Row icon={ExternalLink} label="LeadConnector contact">
              <code className={`text-[11px] ${textSecondary}`}>{cb.ghl_contact_id}</code>
            </Row>
          )}
          {cb.failure_reason && (
            <div className="rounded-lg p-3 bg-red-500/10 border border-red-500/30" data-testid="failure-reason">
              <div className="text-[10px] uppercase tracking-wider text-red-300 mb-1">Why it failed</div>
              <div className={`text-sm ${textPrimary}`}>{cb.failure_reason}</div>
            </div>
          )}
          {cb.dialed_at && (cb.status === 'dialing' || cb.status === 'in_progress') && (
            <div className="rounded-lg p-3 bg-cyan-500/10 border border-cyan-500/30">
              <div className="text-[10px] uppercase tracking-wider text-cyan-300 mb-1">Dial in progress</div>
              <div className={`text-xs ${textSecondary}`}>Started {new Date(cb.dialed_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</div>
            </div>
          )}
        </div>

        <div className={`p-4 border-t ${cardBorder} grid grid-cols-2 gap-2`}>
          {isOpen && (
            <>
              <button onClick={onTriggerNow} className="col-span-2 px-3 py-2 rounded-xl bg-teal-400 text-[#08090d] text-sm font-semibold hover:bg-teal-500 transition" data-testid={isText ? 'action-send-now' : 'action-call-now'}>
                {isText ? <><Send size={14} className="inline mr-1" /> Send Now</> : <><PlayCircle size={14} className="inline mr-1" /> Call Now</>}
              </button>
              <button onClick={onEdit} className={`px-3 py-2 rounded-xl ${subtleBg} ${textSecondary} text-xs font-medium hover:text-teal-300 transition border ${cardBorder}`} data-testid="action-edit">
                Edit
              </button>
              <button onClick={onCancel} className="px-3 py-2 rounded-xl border border-rose-400/30 text-rose-300 text-xs font-medium hover:bg-rose-400/10 transition" data-testid="action-cancel">
                <Trash2 size={13} className="inline mr-1" /> Cancel
              </button>
            </>
          )}
          {isFailedLike && (
            <>
              <button onClick={onRetry} className="col-span-2 px-3 py-2 rounded-xl bg-teal-400 text-[#08090d] text-sm font-semibold hover:bg-teal-500 transition" data-testid="action-retry">
                {isText ? <><Send size={14} className="inline mr-1" /> Retry send</> : <><PlayCircle size={14} className="inline mr-1" /> Retry now</>}
              </button>
              <button onClick={onCancel} className={`col-span-2 px-3 py-2 rounded-xl border border-rose-400/30 text-rose-300 text-xs font-medium hover:bg-rose-400/10 transition`} data-testid="action-cancel">
                <Trash2 size={13} className="inline mr-1" /> Cancel
              </button>
            </>
          )}
          {isClosed && (
            <div className={`col-span-2 text-center text-xs ${textSecondary}`}>
              {isText
                ? (cb.status === 'sent' ? <>This textback was delivered. <Send size={11} className="inline" /></> : 'This textback is closed.')
                : <>This callback is closed. {cb.status === 'completed' ? <PhoneIncoming size={11} className="inline" /> : <PhoneMissed size={11} className="inline" />}</>}
            </div>
          )}

          {/* Permanent delete — always available, gated behind a confirm step */}
          <div className={`col-span-2 mt-1 pt-3 border-t ${cardBorder}`}>
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className={`w-full px-3 py-2 rounded-xl text-[11px] font-medium ${textSecondary} hover:text-red-300 hover:bg-red-500/10 transition border border-transparent hover:border-red-500/30`}
                data-testid="action-delete-prompt"
              >
                <Trash2 size={11} className="inline mr-1.5" /> Delete forever
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className={`text-[11px] ${textSecondary} flex-1`}>Permanently remove this row?</span>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className={`px-3 py-1.5 rounded-lg ${subtleBg} ${textSecondary} text-[11px] hover:${textPrimary} border ${cardBorder}`}
                >
                  Keep
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[11px] font-semibold hover:bg-red-600"
                  data-testid="action-delete-confirm"
                >
                  Delete forever
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <Icon size={14} className="text-teal-400 flex-shrink-0 mt-1" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function ScheduleModal({ api, subAccountId, agents, workflows, ghlConnected, cardBorder, textPrimary, textSecondary, inputBg, editing, onClose, onSaved }) {
  const initial = useMemo(() => {
    if (editing) {
      const d = editing.scheduled_at ? new Date(editing.scheduled_at) : new Date();
      return {
        type: (editing.type || 'call') === 'textback' ? 'textback' : 'call',
        contact_name: editing.contact_name || '',
        contact_phone: editing.contact_phone || '',
        agent_id: editing.agent_id || '',
        scheduled_date: localKey(d),
        scheduled_time: `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`,
        notes: editing.notes || '',
        timezone: editing.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        ghl_contact_id: editing.ghl_contact_id || null,
        trigger_workflow_on_miss: !!editing.trigger_workflow_on_miss,
        ghl_workflow_id: editing.ghl_workflow_id || '',
      };
    }
    return {
      type: 'call',
      contact_name: '', contact_phone: '', agent_id: '',
      scheduled_date: '', scheduled_time: '', notes: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ghl_contact_id: null, trigger_workflow_on_miss: false, ghl_workflow_id: '',
    };
  }, [editing]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [pickedContact, setPickedContact] = useState(() => (initial.ghl_contact_id ? { id: initial.ghl_contact_id, name: initial.contact_name, phone: initial.contact_phone } : null));
  // Calls need a voice agent; textbacks can use any agent (its persona writes the message)
  const eligibleAgents = form.type === 'textback' ? agents : agents.filter(a => a.type === 'voice');

  const pickContact = (c) => {
    setForm(f => ({ ...f, contact_name: c.name || '', contact_phone: c.phone || '', ghl_contact_id: c.ghl_contact_id || c.id || null }));
    setPickedContact(c);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agent_id) { toast.error('Select an agent'); return; }
    if (!form.contact_phone) { toast.error('Enter a phone number'); return; }
    if (!form.scheduled_date || !form.scheduled_time) { toast.error('Pick a date & time'); return; }
    if (form.trigger_workflow_on_miss && !form.ghl_workflow_id) { toast.error('Pick a workflow or disable the toggle'); return; }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        agent_id: form.agent_id,
        scheduled_at: `${form.scheduled_date}T${form.scheduled_time}:00`,
        notes: form.notes,
        timezone: form.timezone,
        ghl_contact_id: form.ghl_contact_id,
        trigger_workflow_on_miss: form.type === 'call' ? form.trigger_workflow_on_miss : false,
        ghl_workflow_id: form.type === 'call' && form.trigger_workflow_on_miss ? form.ghl_workflow_id : null,
      };
      if (editing) {
        await api.put(`/api/sub-accounts/${subAccountId}/scheduled-calls/${editing.call_id || editing.scheduled_call_id}`, payload);
        toast.success(form.type === 'textback' ? 'Textback updated' : 'Callback updated');
      } else {
        await api.post(`/api/sub-accounts/${subAccountId}/scheduled-calls`, payload);
        toast.success(form.type === 'textback' ? 'Textback scheduled' : 'Callback scheduled');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
        className={`w-full max-w-lg my-6 rounded-2xl bg-white dark:bg-[#0c0d12] border ${cardBorder} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 border-b ${cardBorder} flex items-center justify-between`}>
          <h3 className={`text-base font-semibold ${textPrimary}`}>{editing ? (form.type === 'textback' ? 'Edit Textback' : 'Edit Callback') : 'Schedule Callback/Textback'}</h3>
          <button onClick={onClose} className={`p-1 ${textSecondary} hover:${textPrimary}`}><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Call / Text toggle */}
          <div className={`flex rounded-xl ${inputBg} border ${cardBorder} p-1`} data-testid="followup-type-toggle">
            {[['call', 'Call', Phone], ['textback', 'Text', MessageSquare]].map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                onClick={() => setForm(f => ({ ...f, type: id, agent_id: '' }))}
                disabled={!!editing}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  form.type === id ? (id === 'call' ? 'bg-teal-400 text-[#08090d]' : 'bg-sky-400 text-[#08090d]') : `${textSecondary} hover:${textPrimary}`
                } ${editing ? 'opacity-70 cursor-not-allowed' : ''}`}
                data-testid={`followup-type-${id}`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          {form.type === 'textback' && (
            <p className={`text-[11px] ${textSecondary} -mt-2`}>
              At the scheduled time, the AI composes and sends a short follow-up text from the selected agent. Replies land in the Inbox and the AI keeps the conversation going.
            </p>
          )}
          <div className="relative">
            <label className={`block text-xs ${textSecondary} mb-1.5`}>Select LeadConnector contact</label>
            {ghlConnected ? (
              <CrmContactSelect api={api} fetchUrl={`/api/sub-accounts/${subAccountId}/ghl/contacts/search`} contact={pickedContact}
                onSelect={pickContact} s={{ inputBg, cardBorder, textPrimary, textSecondary }} testId="callback-contact" />
            ) : (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cardBorder} bg-amber-400/5`}>
                <AlertCircle size={14} className="text-amber-400 flex-shrink-0" />
                <div className={`text-xs ${textSecondary}`}>Connect LeadConnector to search and attach contacts. You can still enter the name and phone manually below.</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs ${textSecondary} mb-1.5`}>Name</label>
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="John Doe"
                className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                data-testid="callback-name-input"
              />
            </div>
            <div>
              <label className={`block text-xs ${textSecondary} mb-1.5`}>Phone</label>
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                placeholder="+15551234567"
                className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                required
                data-testid="callback-phone-input"
              />
            </div>
          </div>

          <div>
            <label className={`block text-xs ${textSecondary} mb-1.5`}>{form.type === 'textback' ? 'Agent (writes & sends the text)' : 'Voice agent'}</label>
            <select value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
              required data-testid="callback-agent-select"
            >
              <option value="">— choose —</option>
              {eligibleAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs ${textSecondary} mb-1.5`}>Date</label>
              <input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                required data-testid="callback-date-input"
              />
            </div>
            <div>
              <label className={`block text-xs ${textSecondary} mb-1.5`}>Time</label>
              <input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                required data-testid="callback-time-input"
              />
            </div>
          </div>

          <div>
            <label className={`block text-xs ${textSecondary} mb-1.5`}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional context for the agent…"
              rows={2}
              className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
              data-testid="callback-notes-input"
            />
          </div>

          {/* LeadConnector workflow on miss — calls only */}
          {ghlConnected && form.type === 'call' && (
            <div className={`rounded-xl p-3 border ${cardBorder} ${inputBg} space-y-2`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.trigger_workflow_on_miss}
                  onChange={(e) => setForm({ ...form, trigger_workflow_on_miss: e.target.checked })}
                  className="w-4 h-4 rounded" data-testid="callback-workflow-toggle"
                />
                <Workflow size={14} className="text-teal-400" />
                <div className="flex-1">
                  <div className={`text-xs font-semibold ${textPrimary}`}>Trigger LeadConnector workflow if missed</div>
                  <div className={`text-[10px] ${textSecondary}`}>Adds the contact to the workflow when this callback is marked Missed.</div>
                </div>
              </label>
              {form.trigger_workflow_on_miss && (
                <select value={form.ghl_workflow_id} onChange={(e) => setForm({ ...form, ghl_workflow_id: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                  data-testid="callback-workflow-select"
                >
                  <option value="">— pick a workflow —</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-sm hover:${textPrimary}`}>Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-teal-400 text-[#08090d] text-sm font-semibold hover:bg-teal-500 disabled:opacity-50" data-testid="callback-submit-btn">
              {saving ? <><Loader2 size={13} className="inline mr-1 animate-spin" /> Saving…</> : (editing ? 'Save changes' : 'Schedule')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const ScheduledCallsPage = () => (
  <SubAccountLayout>
    <ScheduledCallsContent />
  </SubAccountLayout>
);

function CancelConfirmModal({ cb, cardBorder, textPrimary, textSecondary, inputBg, onClose, onConfirm }) {
  const [val, setVal] = useState('');
  const ok = val.trim() === 'CANCEL';
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cancel-confirm-modal"
    >
      <motion.div
        initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
        className={`w-full max-w-md rounded-2xl bg-white dark:bg-[#0c0d12] border ${cardBorder} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 border-b ${cardBorder} flex items-center gap-2`}>
          <Trash2 size={16} className="text-rose-400" />
          <h3 className={`text-base font-semibold ${textPrimary}`}>Cancel {isTextbackRow(cb) ? 'Textback' : 'Callback'}</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className={`text-sm ${textPrimary}`}>
            You are about to cancel the {isTextbackRow(cb) ? 'textback' : 'callback'} for{' '}
            <span className="font-semibold text-rose-300">{cb.contact_name || cb.contact_phone || cb.phone_number || '(no name)'}</span>{' '}
            scheduled for{' '}
            <span className="font-semibold">
              {cb.scheduled_at ? new Date(cb.scheduled_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
            </span>.
          </div>
          <div className={`text-xs ${textSecondary}`}>
            This is destructive and cannot be undone. Type <span className="font-bold text-rose-300">CANCEL</span> below to confirm.
          </div>
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Type CANCEL"
            autoFocus
            className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm tracking-widest font-mono focus:outline-none focus:border-rose-400/60`}
            data-testid="cancel-confirm-input"
          />
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-sm hover:${textPrimary}`}
              data-testid="cancel-confirm-back"
            >
              Keep {isTextbackRow(cb) ? 'textback' : 'callback'}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!ok}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                ok ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-rose-500/30 text-rose-200/60 cursor-not-allowed'
              }`}
              data-testid="cancel-confirm-submit"
            >
              Cancel {isTextbackRow(cb) ? 'textback' : 'callback'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default ScheduledCallsPage;
